/**
 * DeleteConfirmModal - Reusable delete confirmation modal
 */

import React from 'react';

const DeleteConfirmModal = ({
    title = 'Confirm Delete',
    message,
    confirmLabel = 'Delete',
    cancelLabel = 'Cancel',
    isDestructive = true,
    isLoading = false,
    canConfirm = true,
    error = null,
    onConfirm,
    onCancel,
}) => {
    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            {/* Backdrop */}
            <div 
                className="fixed inset-0 bg-black/50 transition-opacity"
                onClick={onCancel}
            />

            {/* Modal */}
            <div className="flex min-h-full items-center justify-center p-4">
                <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden">
                    {/* Content */}
                    <div className="p-6">
                        {/* Icon */}
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 ${
                            isDestructive ? 'bg-red-100' : 'bg-amber-100'
                        }`}>
                            <svg 
                                className={`w-6 h-6 ${isDestructive ? 'text-red-600' : 'text-amber-600'}`} 
                                fill="none" 
                                stroke="currentColor" 
                                viewBox="0 0 24 24"
                            >
                                <path 
                                    strokeLinecap="round" 
                                    strokeLinejoin="round" 
                                    strokeWidth={2} 
                                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" 
                                />
                            </svg>
                        </div>

                        {/* Title */}
                        <h3 className="text-lg font-display font-bold text-gray-900 text-center mb-2">
                            {title}
                        </h3>

                        {/* Message */}
                        <p className="text-sm text-gray-600 text-center">
                            {message}
                        </p>

                        {/* Error */}
                        {error && (
                            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
                                {error}
                            </div>
                        )}
                    </div>

                    {/* Actions */}
                    <div className="px-6 pb-6 flex items-center justify-center gap-3">
                        <button
                            type="button"
                            onClick={onCancel}
                            disabled={isLoading}
                            className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors disabled:opacity-50"
                        >
                            {cancelLabel}
                        </button>
                        <button
                            type="button"
                            onClick={onConfirm}
                            disabled={isLoading || !canConfirm}
                            className={`flex-1 px-4 py-2.5 text-sm font-medium text-white rounded-xl transition-colors disabled:opacity-50 flex items-center justify-center gap-2 ${
                                isDestructive 
                                    ? 'bg-red-600 hover:bg-red-700' 
                                    : 'bg-emerald-600 hover:bg-emerald-700'
                            }`}
                        >
                            {isLoading && (
                                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white/30 border-t-white"></div>
                            )}
                            {confirmLabel}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DeleteConfirmModal;
